import { Form, Modal, Select, Skeleton, Typography } from 'antd';
import React, { useCallback, useContext, useEffect } from 'react';

import { CANCEL, RULES_IS_REQUIRE, SAVE } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { CHANGE_EMPLOYEE_TITLE } from '@app/pages/TasksList/constants/modalTitles';
import { NotificationContext } from '@app/providers';
import { useTaskListContext } from '@app/providers/TaskListProvider';
import { handleCatchError } from '@app/services';
import {
  useSearchCrmTaskUsersQuery,
  useSetOwnersForCrmTasksMutation,
} from '@app/services/rtkQuery/CrmTasks/api';

const { Text } = Typography;
const { useForm, Item } = Form;

interface FormType {
  OwnerIds: number;
}

export const ChangeEmployeeModal: React.FC = () => {
  const {
    isChangeEmployeeModalOpen,
    closeChangeEmployeeModal,
    selectedRowKeys,
    searchTasks,
    skipSelectedRows,
  } = useTaskListContext();
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();

  const [form] = useForm<FormType>();

  const {
    data: tasksUsers,
    isLoading: isUsersLoading,
    error: usersLoadingError,
  } = useSearchCrmTaskUsersQuery(
    {
      searchTerm: '',
      limit: 0,
      ActiveBranchId: activeBranchId,
    },
    {
      skip: !activeBranchId || !isChangeEmployeeModalOpen,
    }
  );

  const [setOwnersForCrmTasks, { isLoading: isSetOwnersForCrmTasksLoading }] =
    useSetOwnersForCrmTasksMutation();

  useEffect(() => {
    if (usersLoadingError) {
      showNotification(handleCatchError(usersLoadingError));
    }
  }, [showNotification, usersLoadingError]);

  const handleClose = useCallback(() => {
    form.resetFields();
    closeChangeEmployeeModal();
  }, [closeChangeEmployeeModal, form]);

  const handleSave = useCallback(
    async (values: FormType) => {
      const { OwnerIds } = values;
      try {
        await setOwnersForCrmTasks({
          ActiveBranchId: activeBranchId,
          data: {
            CrmTaskIds: selectedRowKeys.map((item) => Number(item)),
            OwnerIds: [OwnerIds],
          },
        }).unwrap();

        form.resetFields();
        handleClose();
        searchTasks();
        skipSelectedRows();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      activeBranchId,
      form,
      handleClose,
      showNotification,
      searchTasks,
      selectedRowKeys,
      setOwnersForCrmTasks,
      skipSelectedRows,
    ]
  );

  return (
    <Modal
      open={isChangeEmployeeModalOpen}
      title={CHANGE_EMPLOYEE_TITLE}
      className="task-modal"
      onCancel={handleClose}
      okText={SAVE}
      cancelText={CANCEL}
      onOk={form.submit}
      okButtonProps={{
        loading: isSetOwnersForCrmTasksLoading,
        disabled: isUsersLoading,
      }}
      cancelButtonProps={{
        disabled: isUsersLoading || isSetOwnersForCrmTasksLoading,
      }}
    >
      <Form form={form} onFinish={handleSave}>
        {isUsersLoading ? (
          <Skeleton />
        ) : (
          <div className="container">
            <div className="item-container">
              <Text strong className="label label-require">
                Ответственный:
              </Text>
              <Item rules={RULES_IS_REQUIRE} name="OwnerIds">
                <Select
                  size="large"
                  allowClear
                  optionFilterProp="label"
                  optionLabelProp="label"
                  showSearch={true}
                  options={(tasksUsers || []).map(
                    ({ UserId, NameFirst, NameLast }) => {
                      return {
                        value: UserId,
                        label: `${NameLast || ''} ${NameFirst || ''}`.trim(),
                      };
                    }
                  )}
                />
              </Item>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};
